import React, { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import "./StyledInputMultiFile.scss";

interface StyledInputMultiFileProps {
  label: string;
  setValue: (files: File[]) => void;
  value?: File[];
  required?: boolean;
  accept?: string;
  className?: string;
  maxFiles?: number;
}

const StyledInputMultiFile: React.FC<StyledInputMultiFileProps> = ({
  label,
  setValue,
  value = [],
  required = false,
  accept,
  className,
  maxFiles = 30,
}) => {
  const [files, setFiles] = useState<File[]>(value);
  const [previews, setPreviews] = useState<
    { file: File; preview: string; type: string }[]
  >([]);

  useEffect(() => {
    setFiles(value);
    generatePreviews(value);
  }, [value]);

  const generatePreviews = (fileList: File[]) => {
    const newPreviews = fileList.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type,
    }));
    setPreviews(newPreviews);
  };

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const newFiles = [...files, ...selectedFiles].slice(0, maxFiles);

    setFiles(newFiles);
    setValue(newFiles);
    generatePreviews(newFiles);

    // Reset input
    e.target.value = "";
  };

  const handleFileRemove = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setValue(newFiles);
    generatePreviews(newFiles);

    // Clean up preview URL
    if (previews[index]) {
      URL.revokeObjectURL(previews[index].preview);
    }
  };

  const renderFilePreview = (
    preview: { file: File; preview: string; type: string },
    index: number,
  ) => {
    const { file, preview: previewUrl, type } = preview;

    return (
      <div key={index} className="file-preview-item">
        <div className="file-preview-content">
          {type === "application/pdf" ? (
            <embed src={previewUrl} width="150" height="100" />
          ) : type.startsWith("image/") ? (
            <img
              src={previewUrl}
              alt={`Aperçu ${file.name}`}
              style={{
                maxWidth: "150px",
                maxHeight: "100px",
                objectFit: "cover",
              }}
            />
          ) : (
            <div className="file-placeholder">
              <span>{file.name}</span>
            </div>
          )}
        </div>
        <div className="file-info">
          <span className="file-name">{file.name}</span>
          <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
        </div>
        <button
          type="button"
          onClick={() => handleFileRemove(index)}
          className="remove-file-btn"
          aria-label={`Supprimer ${file.name}`}
        >
          <FiTrash2 />
        </button>
      </div>
    );
  };

  return (
    <div className={`styled-input-multi-file ${className || ""}`}>
      <label className="input-label">
        {label}
        {required && <span className="required">*</span>}
      </label>

      <div className="files-container">
        {previews.map((preview, index) => renderFilePreview(preview, index))}

        {files.length < maxFiles && (
          <div className="add-file-section">
            <label className="add-file-btn">
              <FiPlus />
              <span>Ajouter des fichiers</span>
              <input
                type="file"
                multiple
                onChange={handleFileAdd}
                accept={accept}
                style={{ display: "none" }}
              />
            </label>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="files-summary">
          {files.length} fichier{files.length > 1 ? "s" : ""} sélectionné
          {files.length > 1 ? "s" : ""}
          {files.length > 1 && (
            <div className="auto-combine-notice">
              <small>Les fichiers seront automatiquement combinés en PDF</small>
            </div>
          )}
        </div>
      )}

      {files.length >= maxFiles && (
        <div className="max-files-notice">
          Limite de {maxFiles} fichiers atteinte
        </div>
      )}
    </div>
  );
};

export default StyledInputMultiFile;
